import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ChangeDetectionStrategy,
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import {
	catchError,
	finalize,
	firstValueFrom,
	Observable,
	of,
	tap,
} from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { EditDocusignEnvelopeComponent } from './edit-docusign-envelope/edit-docusign-envelope.component';
import { Loading } from '@lmc-app/applications/loading';
import { UserService } from '@lmc-app/core/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDocusignDialogComponent } from './confirm-docusign-dialog/confirm-docusign-dialog.component';
import { DocusignEnvelope } from './interface/docusign-envelope-interface';
import { DocusignEnvelopeDetails } from './interface/docusign-envelope-details-interface';
import { DocusignEnvelopeRecipients } from './interface/docusign-envelope-recipients-interface';
import { DocusignEnvelopeContents } from './interface/docusign-envelope-contents-interface';
import {
	TEMPLATE_NAMES,
	APPLICANT_TYPE,
	ENVELOPE_ROLES,
	ALLOWED_SEND,
	ALLOWED_RESEND,
	ALLOWED_MANUAL_UPLOAD,
	FIXED_TEXT_OPTIONS,
	DOCUSIGN_ALLOWED_VOID,
} from './constants/docusign-constants';
import { PreviewDocusignModalComponent } from './preview-docusign-modal/preview-docusign-modal.component';
import { DocusignEnvelopeNLSDetails } from './interface/docusign-envelope-nls-details';
import {
	addEnvelopeRecipients,
	callLoader,
	cleanPhone,
} from './helper-functions/docusign-utils';
import { DocusignDeliveryOptionComponent } from './docusign-delivery-option/docusign-delivery-option.component';
import { LoanAmendmentDialogComponent } from './loan-amendment-dialog/loan-amendment-dialog.component';
import { LoanAmendmentDialogResult } from './interface/docusign-loan-amendment-interface';
import { AgreementsVoidComponent } from './agreements/agreements-void/agreements-void.component';
import {
	NgIf,
	NgFor,
	NgClass,
	AsyncPipe,
	UpperCasePipe,
	DatePipe,
} from '@angular/common';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
	MatMenuTrigger,
	MatMenu,
	MatMenuItem,
	MatMenuContent,
} from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';

@Component({
	selector: 'app-docusign',
	templateUrl: './docusign.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./docusign.component.scss'],
	imports: [
		NgIf,
		NgbAlert,
		MatCard,
		MatCardContent,
		MatButton,
		MatMenuTrigger,
		MatIcon,
		MatMenu,
		MatMenuItem,
		NgFor,
		MatProgressSpinner,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		NgClass,
		MatIconButton,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenuContent,
		AsyncPipe,
		UpperCasePipe,
		DatePipe,
	],
})
export class DocusignComponent implements OnInit, OnChanges {
	@Input() application: any;
	@Input() currentUser: User | null = null;
	@Output() applicationChange = new EventEmitter<any>();
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _bottomSheet = inject(MatBottomSheet);
	safeUrl: SafeResourceUrl | null = null;
	readonly tableHeaders: string[] = [
		'applicantName',
		'applicantType',
		'clientEmail',
		'templateName',
		'status',
		'createdBy',
		'createdAt',
		'sentBy',
		'sentAt',
		'actions',
	];
	envelope$!: Observable<any>;
	selectedRow: any;
	primarySob: any[] = [];
	coAppSob: any[] = [];
	showPreview: boolean = false;
	isCollapsed = true;
	isLoading = false;
	createdEnvelopeResponse: any;
	dataToUpdate: any;
	selectedRowData: any = null;
	selectedActionIndex: number = -1;

	// // docusignEnvelopeRecipients: DocusignEnvelopeRecipients | undefined;   // Defined Envelope Recipients
	// // docusignEnvelopeContents: DocusignEnvelopeContents | undefined;   // Defined Envelope Contents
	// // docusignEnvelope: DocusignEnvelope | undefined;   // Defined Envelope Main
	// // docusignEnvelopeDetails: DocusignEnvelopeDetails | undefined;   // Defined Envelope Sub-details
	docusignEnvelopeNLSDetails: DocusignEnvelopeNLSDetails | undefined; // NLS details

	private readonly _dialog = inject(MatDialog);

	// Constants
	readonly _templateName = TEMPLATE_NAMES;
	readonly appType = APPLICANT_TYPE;
	readonly envelopeRole = ENVELOPE_ROLES;
	readonly allowSend = ALLOWED_SEND;
	readonly allowResend = ALLOWED_RESEND;
	readonly allowManualUpload = ALLOWED_MANUAL_UPLOAD;
	readonly fixedTextOptions = FIXED_TEXT_OPTIONS;
	readonly allowVoid = DOCUSIGN_ALLOWED_VOID;

	pdfSource: Uint8Array | null = null;
	isPanelExpanded = false;
	hasEnvelopes: boolean = false;
	errorMessage: string = '';
	selectedTemplateName: string = '';
	currentSelecteEnvelopeId: string = '';
	loanHasLpp: boolean = false; // LPP Cancellation

	expandedElement: DocusignEnvelopeDetails | null = null;
	expandedColumnHeaders: string[] = [
		'envelope_id',
		'main_applicant_name',
		'main_applicant_email',
		'co_applicant_firstname',
		'co_applicant_lastname',
		'co_applicant_email',
		'speak_on_behalf_name',
		'speak_on_behalf_relationship',
		'loan_number',
		'policy_type',
		'policy_number',
		'agency',
		'creditor',
	];
	fixedDeliveryOptions: string[] = [''];
	loanAmendmentTypes: string[] = ['Frequency', 'Interest Rate'];

	constructor(
		private _docusignService: DocusignService,
		private readonly userService: UserService,
		private _previewDialog: MatDialog,
		private _deliveryDialog: MatDialog
	) {
		this.getClientEnvelopes();
	}

	// Getter methods for template
	get showStateDisplay(): boolean {
		return this.isLoading || !!this.hasEnvelopes;
	}

	get stateType(): 'loading' | 'empty' | 'error' {
		if (this.isLoading) return 'loading';
		// if (this.hasError) return 'error';
		return 'empty';
	}

	get showTable(): boolean {
		return !this.isLoading && this.hasEnvelopes;
	}

	ngOnInit(): void {
		// this.userService.user$.subscribe((user) => {
		//     this.currentUser = user;
		// });
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.application) {
			this.application = changes.application?.currentValue;
			if (this.application) {
				this.getClientEnvelopes();
				this.speakOnBehalfList();
			}
			if (changes.currentUser?.currentValue) {
				this.currentUser = changes.currentUser?.currentValue;
				console.log(this.currentUser, '+++++');
				this.getDocusignLPPCancellation();
			}
		}
	}

	mapDocusignEnvelopeRecipients(
		applicantType: string,
		application: any,
		roleName: string,
		recipientId: string,
		channelType: any,
		templateType?: string
	) {
		const envelopeOwnerName =
			applicantType?.toLowerCase() === this.appType?.PRIMARY
				? application?.Deal_Name
				: application?.Co_Applicant_Name?.concat(' ')?.concat(
						application?.Guarantor_Name
					);
		const emailAddress =
			applicantType?.toLowerCase() === this.appType?.PRIMARY
				? application?.Email1
				: application?.Email;
		const phoneNumber =
			applicantType?.toLowerCase() === this.appType?.PRIMARY
				? application?.Mobile
				: application?.['Secondary_Cell_Number'];
		const templateLabel = this.getTemplateLabel(templateType);
		// Co-applicant Consent Form / Speak on Behalf are addressed to the co-applicant, not the primary
		const isCoAppOwnEnvelope =
			applicantType?.toLowerCase() === this.appType?.COAPP &&
			(
				[this._templateName.CONSENT, this._templateName.SOB] as string[]
			).includes(templateType?.toLowerCase() ?? '');
		const dealName = isCoAppOwnEnvelope
			? envelopeOwnerName?.toUpperCase()
			: this.application?.Deal_Name?.toUpperCase();
		// const coAppPhoneNumber = application?.Secondary_Cell_Number;
		const docusignEnvelopeRecipients: Partial<DocusignEnvelopeRecipients> = {
			name: envelopeOwnerName?.toUpperCase(),
			...(['default', 'multi']?.includes(
				channelType?.deliveryType?.toLowerCase()
			)
				? {
						email: emailAddress,
						...(templateLabel
							? {
									emailNotification: {
										emailSubject: `${templateLabel} - ${dealName}`,
										emailBody: `Hi ${envelopeOwnerName?.toUpperCase()},<br><br>Please review and sign, ${templateLabel?.toLowerCase()} with LM Credit Inc.<br><br>Thank you`,
										supportedLanguage: 'en',
									},
								}
							: {}),
					}
				: {}),
			roleName: roleName,
			recipientId: recipientId,
			...(channelType?.deliveryType?.toLowerCase() !== 'default'
				? this.createDeliveryChannel(phoneNumber, channelType)
				: {}),
		};
		return docusignEnvelopeRecipients;
	}

	mapDocusignEnvelopeContents(
		applicantType: string,
		application: any,
		speakOnBehalf: any
	) {
		const docusignEnvelopeContents: Partial<DocusignEnvelopeContents> = {
			isCoApp:
				applicantType?.toLowerCase() === this.appType?.PRIMARY ? 'No' : 'Yes',
			DealName: application?.Deal_Name?.toUpperCase(),
			CoSignerName: application?.Co_Applicant_Name?.concat(' ')
				?.concat(application?.Guarantor_Name)
				?.toUpperCase(),
			SOBRelationship: speakOnBehalf?.['Relation_to_Applicant']?.toLowerCase(),
			SOBFullName: speakOnBehalf?.['Full_Name']?.toUpperCase(),
		};
		return docusignEnvelopeContents;
	}

	mapDocusignEnvelope(
		applicantType: string,
		application: any,
		speakOnBehalf: any,
		selectedTemplate: string,
		channelType: any
	) {
		const dbDisplayName =
			selectedTemplate?.toLowerCase() === this._templateName?.SOB
				? speakOnBehalf?.Full_Name
				: applicantType?.toLowerCase() === this.appType?.PRIMARY
					? application?.Deal_Name
					: application?.Co_Applicant_Name?.concat(' ')?.concat(
							application?.Guarantor_Name
						);
		const emailAddress =
			applicantType?.toLowerCase() === this.appType?.PRIMARY
				? application?.Email1
				: application?.Email;
		const docusignEnvelope: DocusignEnvelope = {
			application_id: application?.id,
			applicant_type: applicantType?.toUpperCase(),
			template_name: selectedTemplate?.toLowerCase(),
			name: dbDisplayName?.toUpperCase(),
			client_email: emailAddress,
			via_email:
				channelType?.deliveryType?.toLowerCase() === 'default' ||
				channelType?.deliveryOptions?.split('+')?.[0]?.toLowerCase() === 'email'
					? true
					: false,
			via_sms:
				channelType?.deliveryOptions?.split('+')?.[1]?.toLowerCase() === 'sms'
					? true
					: false,
			via_whatsapp:
				channelType?.deliveryOptions?.split('+')?.[1]?.toLowerCase() ===
				'whatsapp'
					? true
					: false,
		};
		return docusignEnvelope;
	}

	mapDocusignEnvelopeDetails(application: any, speakOnBehalf: any) {
		const docusignEnvelopeDetails: Partial<DocusignEnvelopeDetails> = {
			application_id: application?.id,
			main_applicant_name: application?.Deal_Name?.toUpperCase(),
			main_applicant_email: application?.Email1,
			co_applicant_firstname: application?.Co_Applicant_Name?.toUpperCase(),
			co_applicant_lastname: application?.Guarantor_Name?.toUpperCase(),
			co_applicant_email: application?.Email,
			speak_on_behalf_zoho_id: speakOnBehalf?.['id'],
			speak_on_behalf_name: speakOnBehalf?.['Full_Name']?.toUpperCase(),
			speak_on_behalf_relationship:
				speakOnBehalf?.['Relation_to_Applicant']?.toLowerCase(),
			work_drive_id: application?.zohoworkdriveforcrm__Workdrive_Folder_ID,
			agency: application?.Account_Name?.['name'],
			creditor: application?.Creditt,
			main_applicant_phone: cleanPhone(application?.Mobile),
			co_applicant_phone: cleanPhone(application?.Secondary_Cell_Number),
		};
		return docusignEnvelopeDetails;
	}

	highlight(row: any) {
		this.selectedRow = row;
	}

	hasCoApp() {
		console.log(this.application?.Co_Appl_Required);
		return this.application?.Co_Appl_Required === 'Yes' ? true : false;
	}

	validateEmailOnCreate(applicant_type: string) {
		const emailExist =
			applicant_type?.toLowerCase() === this.appType?.PRIMARY
				? this.application?.Email1
				: this.application?.Email;
		return emailExist ? true : false;
	}

	async createEnvelope(templateType: string, applicant_type: string) {
		if (this.validateEmailOnCreate(applicant_type)) {
			const _deliveryRes = await this.openDeliveryModal();
			// console.log(_deliveryRes)
			if (!!_deliveryRes) {
				const channelType = this.identifyChannel(_deliveryRes);
				if (
					!this.validateDeliveryPhoneNumber(
						channelType,
						templateType,
						applicant_type
					)
				) {
					let formattedEnvelopeData = null;
					if (templateType === this._templateName?.CONSENT) {
						formattedEnvelopeData = this.mapConsent(
							templateType,
							applicant_type,
							channelType
						);
					} else if (templateType === this._templateName?.LPPCANCELLATION) {
						formattedEnvelopeData = this.mapLppCancellation(
							templateType,
							applicant_type,
							channelType
						);
					} else if (templateType === this._templateName?.AOB) {
						formattedEnvelopeData = this.mapAOB(
							templateType,
							applicant_type,
							channelType
						);
					}
					console.log(_deliveryRes, 'Has Data');
					this.createDocusignEnvelope(formattedEnvelopeData, this.currentUser);
				} else {
					this.errorMessage = `Phone number is missing.`;
				}
			}
		} else {
			this.errorMessage = `Email address missing.`;
		}
	}

	// True: No phone number
	validateDeliveryPhoneNumber(
		channelType: any,
		template_name: string,
		applicantType: string
	) {
		const hasPhoneDelivery =
			this.fixedTextOptions?.includes(
				channelType?.deliveryOptions?.toLowerCase()
			) ||
			this.fixedTextOptions?.includes(
				channelType?.deliveryOptions?.split('+')?.[1]?.toLowerCase()
			)
				? true
				: false;
		if (hasPhoneDelivery) {
			if (template_name?.toLowerCase() === this._templateName?.AOB) {
				return (!this.application?.Mobile ||
					!this.application?.Secondary_Cell_Number) === false
					? false
					: true;
			} else {
				if (applicantType?.toLowerCase() === this.appType?.PRIMARY) {
					return !this.application?.Mobile ? true : false;
				} else {
					return !this.application?.Secondary_Cell_Number ? true : false;
				}
			}
		} else {
			return false;
		}
	}

	identifyChannel(deliverySelected: string) {
		return deliverySelected?.toLocaleLowerCase() === 'email'
			? { deliveryType: 'default', deliveryOptions: deliverySelected }
			: deliverySelected?.includes('+')
				? { deliveryType: 'multi', deliveryOptions: deliverySelected }
				: { deliveryType: 'single', deliveryOptions: deliverySelected };
	}

	consentData(applicant_type: string) {
		if (applicant_type?.toLowerCase() === 'primary') {
			this.application.clientRoleName = 'Client';
			this.application.applicant_type = 'Primary';
			this.application.DealName = this.application?.Deal_Name?.toUpperCase();
			this.application.isCoApp = 'No';
		} else {
			this.application.coSignerRoleName = 'Co-Signer';
			this.application.applicant_type = 'Co-Applicant';
			this.application.CoSignerName =
				this.application?.Co_Applicant_Name?.concat(' ')
					?.concat(this.application?.Guarantor_Name)
					?.toUpperCase();
			this.application.co_applicant_first_name =
				this.application?.Co_Applicant_Name?.toUpperCase();
			this.application.co_applicant_last_name =
				this.application?.Guarantor_Name?.toUpperCase();
			this.application.isCoApp = 'Yes';
		}
	}

	async openDeliveryModal(): Promise<any> {
		const dialogRef = this._deliveryDialog.open(
			DocusignDeliveryOptionComponent,
			{
				maxHeight: '100px',
				width: '450px',
				position: {
					top: '250px',
					left: '30%',
				},
				autoFocus: true,
				disableClose: false,
				panelClass: 'hide-dialog-scrollbar',
			}
		);

		const result = await firstValueFrom(dialogRef.afterClosed());
		return result || null;
	}

	createDocusignEnvelope(envelopeData, userInfo) {
		this.createdEnvelopeResponse = null;
		this.currentSelecteEnvelopeId = '';
		const ref = callLoader(this._bottomSheet, 'Creating envelope');
		this.isLoading = true;
		this._docusignService
			.createEnvelope({ envelopeData: envelopeData, userInfo: userInfo })
			.pipe(
				finalize(() => {
					this.isLoading = false;
					ref.dismiss();
				})
			)
			.subscribe({
				next: (response) => {
					this.openPreviewWindow(response);
					this.getClientEnvelopes();
				},
				error: (err) => {
					this.openSnackBar(
						'error',
						`Create Envelope failed:  ${err?.message || err}`
					);
				},
			});
	}

	createDeliveryChannel(phoneNumber: string, deilveryOption: any) {
		const deliveryOptionDetails = {
			...(deilveryOption?.deliveryOptions?.includes('+')
				? {
						additionalNotifications: [
							{
								secondaryDeliveryMethod:
									deilveryOption?.deliveryOptions?.split('+')?.[1],
								phoneNumber: {
									countryCode: '1',
									number: cleanPhone(phoneNumber),
								},
							},
						],
					}
				: {
						deliveryMethod: deilveryOption?.deliveryOptions,
						phoneNumber: {
							countryCode: '1',
							number: cleanPhone(phoneNumber),
						},
					}),
		};

		return deliveryOptionDetails;
	}

	getClientEnvelopes() {
		this.envelope$ = this._docusignService
			.getClientEnvelopes(this.application?.id, 'DOCUSIGN')
			.pipe(catchError(() => of([])));
		this.hasEnvelopes = !!this.envelope$;
	}

	getDocusignLPPCancellation() {
		this._docusignService
			.getDocusignLPPCancellation({
				templateName: this._templateName?.LPPCANCELLATION,
				application: this.application,
				userInfo: this.currentUser,
			})
			?.subscribe({
				next: (response) => {
					// console.log(response,"++++")
					this.loanHasLpp = response?.data?.hasLPP;
					this.docusignEnvelopeNLSDetails = response?.data?.loanDetails?.[0];
				},
				error: (err) => {
					this.openSnackBar('error', `LPP Cancellation data failed:  ${err}`);
				},
			});
	}

	openSnackBar(messageType: string, message: string) {
		if (messageType === 'error') {
			return this._snackBar.open(
				message,
				SnackbarConstants.ACTION_DISMISS,
				SnackbarConstants.DURATION_DISMISS_ALIVE
			);
		} else {
			return this._snackBar.open(
				message,
				SnackbarConstants.ACTION_DISMISS,
				SnackbarConstants.DURATION_DISMISS_SHORT
			);
		}
	}

	speakOnBehalfList() {
		const sobList = this.application?.Speak_on_Behalf;
		if (sobList) {
			this.primarySob = sobList?.filter(
				(sob) => sob?.Applicant_Type?.toLowerCase() === 'primary'
			);
			this.coAppSob = sobList?.filter(
				(sob) => sob?.Applicant_Type?.toLowerCase() === 'co-applicant'
			);
		}
	}

	uploadToWorkdrive(rowData: any) {
		const ref = callLoader(this._bottomSheet, 'Uploading to workdrive');
		this._docusignService
			?.uploadCompletedDocusign({
				folderId: this.application?.zohoworkdriveforcrm__Workdrive_Folder_ID,
				envelopeId: rowData?.envelope_id,
			})
			.subscribe({
				next: (response) => {
					ref.dismiss();
				},
			});
	}

	async createSpeakOnBehalf(sobData: Event) {
		if (
			this.validateEmailOnCreate(sobData?.['Applicant_Type']?.toLowerCase())
		) {
			const _deliveryRes = await this.openDeliveryModal();

			if (!!_deliveryRes) {
				const channelType = this.identifyChannel(_deliveryRes);
				if (
					!this.validateDeliveryPhoneNumber(
						channelType,
						this._templateName?.SOB,
						sobData?.['Applicant_Type']?.toLowerCase()
					)
				) {
					const channelType = this.identifyChannel(_deliveryRes);
					const mappedSOBEnvelopeRecipients =
						this.mapDocusignEnvelopeRecipients(
							sobData?.['Applicant_Type']?.toLowerCase(),
							this.application,
							this.envelopeRole?.CLIENT,
							'1',
							channelType,
							this._templateName?.SOB
						);
					const mergedMappedSOBEnvelopeRecipients = [];
					addEnvelopeRecipients(
						mergedMappedSOBEnvelopeRecipients,
						mappedSOBEnvelopeRecipients
					);
					const mappedDocusignEnvelopeContents =
						this.mapDocusignEnvelopeContents(
							sobData?.['Applicant_Type'],
							this.application,
							sobData
						);
					const mappedDocusignEnvelope = this.mapDocusignEnvelope(
						sobData?.['Applicant_Type'],
						this.application,
						sobData,
						this._templateName?.SOB,
						channelType
					);
					const mappedDocusignEnvelopeDetails = this.mapDocusignEnvelopeDetails(
						this.application,
						sobData
					);
					const formattedEnvelopeData = {
						...this.application,
						...{
							selectedTemplate: {
								templateType: this._templateName?.SOB?.toLowerCase(),
								applicant_type: sobData?.['Applicant_Type']?.toLowerCase(),
							},
						},
						...{ envelopeRecipients: mergedMappedSOBEnvelopeRecipients },
						...{ envelopeContents: mappedDocusignEnvelopeContents },
						...{ dbMainEnvelope: mappedDocusignEnvelope },
						...{ dbSubEnvelopeDetails: mappedDocusignEnvelopeDetails },
					};
					this.createDocusignEnvelope(formattedEnvelopeData, this.currentUser);
				} else {
					this.errorMessage = `Phone number missing.`;
				}
			}
		} else {
			this.errorMessage = `Email address missing.`;
		}
	}

	// Call this when a row is clicked
	onRowClick(row: any) {
		this.selectedRowData = row;
	}

	onCancel() {
		this.safeUrl = null;
		this.showPreview = false;
		this.selectedActionIndex = -1;
	}

	onRefresh() {
		this.getClientEnvelopes();
	}

	openPreviewWindow(rowData: Event) {
		const dialogRef = this._previewDialog.open(PreviewDocusignModalComponent, {
			maxHeight: '90vh',
			width: '95vw',
			maxWidth: '95vw', // Material caps dialogs at 80vw by default; room for the recipient status panel
			position: {
				top: '40px',
				left: '2.5%',
			},
			autoFocus: true,
			disableClose: true,
			panelClass: 'hide-dialog-scrollbar',
			data: {
				envelopeData: rowData,
				userInfo: this.currentUser,
				application: this.application,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			this.getClientEnvelopes();
		});
	}

	sendEnvelopeAction(selectedActionRow: Event) {
		const sendEnvelopeName = selectedActionRow?.['templateRowId']?.[
			'template_name'
		]?.replaceAll('_', ' ');
		this._dialog
			.open(ConfirmDocusignDialogComponent, {
				data: {
					title: `Do you want to send ${sendEnvelopeName}?`,
					type: '',
					fileName: '',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const ref = callLoader(this._bottomSheet, 'Sending envelope');
					this.isLoading = true;
					const isEdited = !!selectedActionRow?.['modified_at'];
					this._docusignService
						.sendEnvelope({
							envelope_id: selectedActionRow?.['envelope_id'],
							envelopeStatus: selectedActionRow?.['statusId']?.['status_name'],
							envelopeData: selectedActionRow?.['evelopeDetails'],
							envelopeIsEdited: isEdited,
							userInfo: this.currentUser,
						})
						.pipe(
							finalize(() => {
								ref.dismiss();
								this.isLoading = false;
							})
						)
						.subscribe({
							next: (response) => {
								if (response?.status === 200) {
									this.openSnackBar('success', response?.message);
									this.getClientEnvelopes();
								} else {
									this.openSnackBar(
										'error',
										`Send error:  ${response?.message}`
									);
								}
							},
						});
				}
			});
	}

	deleteEnvelopeAction(selectedActionRow: Event) {
		const deleteEnvelopeName = selectedActionRow?.['templateRowId']?.[
			'template_name'
		]?.replaceAll('_', ' ');
		this._dialog
			.open(ConfirmDocusignDialogComponent, {
				data: {
					title: `Do you want to delete ${deleteEnvelopeName}?`,
					type: '',
					fileName: '',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const ref = callLoader(this._bottomSheet, 'Deleting envelope');
					this.isLoading = true;
					this._docusignService
						.deleteEnvelope({
							envelopeData: selectedActionRow,
							userInfo: this.currentUser,
							docusignType: 'DOCUSIGN',
						})
						.pipe(
							finalize(() => {
								ref.dismiss();
								this.isLoading = false;
							})
						)
						.subscribe({
							next: (response) => {
								if (response?.status === 201 || response?.status === 200) {
									this.getClientEnvelopes();
									this.openSnackBar('success', response?.message);
								} else {
									this.openSnackBar('error', response);
								}
							},
						});
				}
			});
	}

	editEnvelopeAction(selectedRow: Event) {
		const editBottomSheet = this._bottomSheet.open(
			EditDocusignEnvelopeComponent,
			{
				data: selectedRow,
			}
		);

		editBottomSheet.afterDismissed().subscribe((data) => {
			if (data) {
				const ref = callLoader(this._bottomSheet, 'Updating envelope.');
				this.isLoading = true;
				const updateData = {
					...data,
					...{ userInfo: this.currentUser },
				};
				this._docusignService
					?.updateEnvelope(updateData)
					.pipe(
						finalize(() => {
							this.isLoading = false;
							this._bottomSheet.dismiss();
						})
					)
					.subscribe({
						next: (response) => {
							const editedData = { jsonData: data };
							this.openPreviewWindow(editedData as unknown as Event);
							this.getClientEnvelopes();
							this.applicationChange.emit(this.application);
						},
						error: (err) => {
							this.openSnackBar('error', `Update Envelope failed:  ${err}`);
						},
					});
			} else {
				return;
			}
		});
	}

	mapConsent(templateType: string, applicant_type: string, channelType: any) {
		this.selectedRowData = null;
		this.createdEnvelopeResponse = null;
		this.dataToUpdate = null;
		const mergedConsentEnvelopeRecipients = [];
		const mappedDocusignEnvelopeRecipients = this.mapDocusignEnvelopeRecipients(
			applicant_type,
			this.application,
			this.envelopeRole?.CLIENT,
			'1',
			channelType,
			templateType
		);
		addEnvelopeRecipients(
			mergedConsentEnvelopeRecipients,
			mappedDocusignEnvelopeRecipients
		);
		const mappedDocusignEnvelopeContents = this.mapDocusignEnvelopeContents(
			applicant_type,
			this.application,
			null
		);
		const mappedDocusignEnvelope = this.mapDocusignEnvelope(
			applicant_type,
			this.application,
			null,
			templateType,
			channelType
		);
		const mappedDocusignEnvelopeDetails = this.mapDocusignEnvelopeDetails(
			this.application,
			null
		);
		const formattedEnvelopeData = {
			...this.application,
			...{
				selectedTemplate: {
					templateType: templateType?.toLowerCase(),
					applicant_type: applicant_type?.toLowerCase(),
				},
			},
			...{ envelopeRecipients: mergedConsentEnvelopeRecipients },
			...{ envelopeContents: mappedDocusignEnvelopeContents },
			...{ dbMainEnvelope: mappedDocusignEnvelope },
			...{ dbSubEnvelopeDetails: mappedDocusignEnvelopeDetails },
		};
		return formattedEnvelopeData;
	}

	mapLppCancellation(
		templateType: string,
		applicant_type: string,
		channelType: any
	) {
		this.selectedRowData = null;
		this.createdEnvelopeResponse = null;
		this.dataToUpdate = null;
		const lmcSignerInfo = {
			Deal_Name: this.currentUser?.full_name,
			Email1: this.currentUser?.email,
		};

		const mappedClientRecipient = this.mapDocusignEnvelopeRecipients(
			applicant_type,
			this.application,
			this.envelopeRole?.CLIENT,
			'1',
			channelType,
			templateType
		);
		const mappedLMCRecipient = this.mapDocusignEnvelopeRecipients(
			applicant_type,
			lmcSignerInfo,
			this.envelopeRole?.LMC,
			'2',
			{ deliveryType: 'default', deliveryOptions: 'email' },
			templateType
		);
		const mergedLppEnvelopeRecipients = [];
		addEnvelopeRecipients(
			mergedLppEnvelopeRecipients,
			mappedClientRecipient,
			mappedLMCRecipient
		);

		const mappedLPPEvelopeContents: Partial<DocusignEnvelopeNLSDetails> = {
			LoanNumber: this.application?.App_Number,
			CreatedDate: this.docusignEnvelopeNLSDetails?.CreatedDate,
			PolicyNumber: this.docusignEnvelopeNLSDetails?.PolicyNumber,
			DealName: this.application?.Deal_Name,
			CoAppFirstName: this.application?.Co_Applicant_Name,
			CoAppLastName: this.application?.Guarantor_Name,
		};

		const mappedDbMainEnvelope = this.mapDocusignEnvelope(
			applicant_type,
			this.application,
			null,
			templateType,
			channelType
		);
		const mappedDbSubEnvelopeDetails = this.mapDocusignEnvelopeDetails(
			this.application,
			null
		);
		const additionalMappedDbSubEnvelopeDetails = {
			...mappedDbSubEnvelopeDetails,
			...{ loan_number: this.docusignEnvelopeNLSDetails?.LoanNumber },
			...{ policy_type: this.docusignEnvelopeNLSDetails?.PolicyType },
			...{ policy_number: this.docusignEnvelopeNLSDetails?.PolicyNumber },
		};
		const formattedEnvelopeData = {
			...{
				selectedTemplate: {
					templateType: templateType?.toLowerCase(),
					applicant_type: applicant_type?.toLowerCase(),
				},
			},
			...{ envelopeRecipients: mergedLppEnvelopeRecipients },
			...{ envelopeContents: mappedLPPEvelopeContents },
			...{ dbMainEnvelope: mappedDbMainEnvelope },
			...{ dbSubEnvelopeDetails: additionalMappedDbSubEnvelopeDetails },
		};
		return formattedEnvelopeData;
	}

	mapAOB(templateType: string, applicant_type: string, channelType: any) {
		this.selectedRowData = null;
		this.createdEnvelopeResponse = null;
		this.dataToUpdate = null;
		const coSignerName = this.application?.Co_Applicant_Name?.concat(
			' '
		)?.concat(this.application?.Guarantor_Name);
		const coSignerInfo = {
			Co_Applicant_Name: this.application?.Co_Applicant_Name,
			Guarantor_Name: this.application?.Guarantor_Name,
			Email: this.application?.Email,
			Secondary_Cell_Number: this.application?.Secondary_Cell_Number,
		};
		const mappedClientRecipient = this.mapDocusignEnvelopeRecipients(
			this.appType?.PRIMARY,
			this.application,
			this.envelopeRole?.CLIENT,
			'1',
			channelType,
			templateType
		);
		const mappedCoSignerRecipient = this.mapDocusignEnvelopeRecipients(
			this.appType?.COAPP,
			coSignerInfo,
			this.envelopeRole?.COSIGNER,
			'2',
			channelType,
			templateType
		);
		const mergedAOBEnvelopeRecipients = [];
		addEnvelopeRecipients(
			mergedAOBEnvelopeRecipients,
			mappedClientRecipient,
			mappedCoSignerRecipient
		);

		const mappedAOBEvelopeContents: Partial<DocusignEnvelopeContents> = {
			DealName: this.application?.Deal_Name,
			Amount: this.application?.Amount,
			CoSignerName: coSignerName,
			Agency: this.application?.Account_Name?.['name'],
			Creditor: this.application?.Creditt,
		};
		const mappedDbMainEnvelope = this.mapDocusignEnvelope(
			applicant_type,
			this.application,
			null,
			templateType,
			channelType
		);
		const mappedDbSubEnvelopeDetails = this.mapDocusignEnvelopeDetails(
			this.application,
			null
		);

		const formattedEnvelopeData = {
			...{
				selectedTemplate: {
					templateType: templateType?.toLowerCase(),
					applicant_type: applicant_type?.toLowerCase(),
				},
			},
			...{ envelopeRecipients: mergedAOBEnvelopeRecipients },
			...{ envelopeContents: mappedAOBEvelopeContents },
			...{ dbMainEnvelope: mappedDbMainEnvelope },
			...{ dbSubEnvelopeDetails: mappedDbSubEnvelopeDetails },
		};
		return formattedEnvelopeData;
	}

	reSendEnvelopeAction(selectedActionRow: Event) {
		const sendEnvelopeName = selectedActionRow?.['templateRowId']?.[
			'template_name'
		]?.replaceAll('_', ' ');
		this._dialog
			.open(ConfirmDocusignDialogComponent, {
				data: {
					title: `Do you want to re-send ${sendEnvelopeName}?`,
					type: '',
					fileName: '',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const ref = callLoader(this._bottomSheet, 'Sending envelope');
					this.isLoading = true;
					this._docusignService
						.reSendEnvelope({
							envelope_id: selectedActionRow?.['envelope_id'],
							userInfo: this.currentUser,
						})
						.pipe(
							finalize(() => {
								ref.dismiss();
								this.isLoading = false;
							})
						)
						.subscribe({
							next: (response) => {
								if (response?.status === 200) {
									this.openSnackBar('success', response?.message);
									this.getClientEnvelopes();
								} else {
									this.openSnackBar(
										'error',
										`Send error:  ${response?.message}`
									);
								}
							},
						});
				}
			});
	}

	clearErrors() {
		this.errorMessage = '';
	}

	// Loan Ammendment
	createLoanAmendment(type: string) {
		this._dialog
			.open(LoanAmendmentDialogComponent, {
				width: '600px',
				maxWidth: '95vw',
				panelClass: 'loan-amendment-modal', // resizable (see styles.scss)
				disableClose: true, // Esc / backdrop click shouldn't discard the steps; use the close button
				autoFocus: 'dialog', // don't focus (and highlight) the close button on open
				data: {
					type,
					application: this.application,
					currentUser: this.currentUser,
				},
			})
			.afterClosed()
			.subscribe((result: LoanAmendmentDialogResult | undefined) => {
				if (result) {
					this.createLoanAmendmentEnvelope(result);
				}
			});
	}

	createLoanAmendmentEnvelope({
		loanAmendment,
		envelopeRecipients,
	}: LoanAmendmentDialogResult) {
		// Delivery flags for the DB record come from the client (first) recipient
		const client = envelopeRecipients?.[0];
		const secondary = (client?.additionalNotifications ?? []).map((n) =>
			n?.secondaryDeliveryMethod?.toLowerCase()
		);
		const primary = client?.deliveryMethod?.toLowerCase();
		const dbMainEnvelope: DocusignEnvelope = {
			...this.mapDocusignEnvelope(
				this.appType?.PRIMARY,
				this.application,
				null,
				'',
				null
			),
			via_email: !!client?.email && primary !== 'sms',
			via_sms: primary === 'sms' || secondary.includes('sms'),
			via_whatsapp: primary === 'whatsapp' || secondary.includes('whatsapp'),
		};
		this.createdEnvelopeResponse = null;
		this.currentSelecteEnvelopeId = '';
		const ref = callLoader(this._bottomSheet, 'Creating loan amendment');
		this.isLoading = true;
		this._docusignService
			.createLoanAmendment({
				loanAmendment,
				envelopeData: {
					envelopeRecipients,
					dbMainEnvelope,
					dbSubEnvelopeDetails: this.mapDocusignEnvelopeDetails(
						this.application,
						null
					),
				},
				userInfo: this.currentUser,
			})
			.pipe(
				finalize(() => {
					this.isLoading = false;
					ref.dismiss();
				})
			)
			.subscribe({
				next: (response) => {
					this.openPreviewWindow(response);
					this.getClientEnvelopes();
				},
				error: (err) => {
					this.openSnackBar(
						'error',
						`Create Loan Amendment failed:  ${err?.error?.message || err?.message || err}`
					);
				},
			});
	}

	voidEnvelopeAction(selectedActionRow: any) {
		this._dialog
			.open(AgreementsVoidComponent, {
				maxHeight: '90vh',
				width: '800px',
				panelClass: 'hide-dialog-scrollbar',
				data: { envelopeId: selectedActionRow?.envelope_id },
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const ref = callLoader(this._bottomSheet, 'Voiding envelope');
					this.isLoading = true;
					this._docusignService
						.voidEnvelope({
							envelopeId: result?.envelopeId,
							userInfo: this.currentUser,
							application: this.application,
							noteDetails: {
								subject: 'Voided DocuSign Envelope',
								description: '-',
								notes: result?.reason,
							},
						})
						.pipe(
							finalize(() => {
								ref.dismiss();
								this.isLoading = false;
							})
						)
						.subscribe({
							next: (response) => {
								if (response) {
									this.openSnackBar(
										'success',
										`Voided: ${response?.envelopeId ?? result?.envelopeId}`
									);
									this.getClientEnvelopes();
								} else {
									this.openSnackBar(
										'error',
										'Error: ENVELOPE_CANNOT_VOID_INVALID_STATE'
									);
								}
							},
							error: (err) => {
								this.openSnackBar(
									'error',
									`Void failed:  ${err?.error?.message || err?.message || err}`
								);
							},
						});
				}
			});
	}

	// Menu label of the selected template, used in the DocuSign email subject/body
	getTemplateLabel(templateType?: string) {
		const templateLabels: Record<string, string> = {
			[this._templateName.CONSENT]: 'Consent Form',
			[this._templateName.SOB]: 'Speak on Behalf',
			[this._templateName.AOB]: 'Apply on Behalf',
			[this._templateName.LPPCANCELLATION]: 'LPP Cancellation',
		};
		return templateLabels[templateType?.toLowerCase() ?? ''] ?? null;
	}
}
